import {_m} from 'INTL';
import SKZPersonalInfo from 'SKZPersonalInfo';
import {Transaction} from 'StoreInfo';
import {NativeModules} from 'react-native';
import SkillzStyle from 'SkillzStyle';
import {Environment} from 'aerosync-react-native-sdk';

const APIManager = NativeModules.SKZAPIManager;

export enum EBankLinkDepositScreen {
  SMSVerify = 'SMSVerify',
  UserInformation = 'AeropayUserInformationScreen',
  MFA = 'AeropayMFAScreen',
  BankLinkWidget = 'AerosyncBankLinkWidget',
}

const AeropayErrorCodes = {
  /**
   * when the user is already registered in the Aeropay network, indicates that SDK should trigger the MFA flow
   */
  USER_ALREADY_EXISTS_IN_NETWORK: 'USER_ALREADY_EXISTS_IN_NETWORK',
  /**
   * shows friendly error message from server side
   */
  AEROPAY_ERROR: 'AEROPAY_ERROR',
} as const;

export class AeropayError extends Error {
  code: keyof typeof AeropayErrorCodes;
  message: string;

  constructor(code: keyof typeof AeropayErrorCodes, message: string) {
    super();
    this.code = code;
    this.message = message;
  }
}

export const AEROPAY_DEFAULT_SUPPORT_EMAIL = 'support@aeropay.com';
export const AEROPAY_DEFAULT_FIRST_DEPOSIT_BONUS_PERCENTAGE = 0;

export const AEROPAY_DEPOSIT_ERROR_MESSAGE = _m(
  "Your deposit didn't go through. Please try another payment method."
);
export const AEROPAY_BANK_LINKING_ERROR_MESSAGE = _m(
  'There was an issue linking your account. Please try again in a couple hours, or contact AeroPay ({0}) for assistance.',
  AEROPAY_DEFAULT_SUPPORT_EMAIL
);
export const AEROPAY_CONTACT_SUPPORT_ERROR_MESSAGE = _m(
  `Your deposit didn't go through. Please contact AeroPay ({0}) for assistance.`,
  AEROPAY_DEFAULT_SUPPORT_EMAIL
);

export type GetAerosyncURLResponse = {
  token: string;
  url: string;
};

export type CreateAeropayUserResponse = {
  shouldShowMFAFlow: boolean;
  userPersonalInfo: SKZPersonalInfo;
} & (
  | {
      shouldShowMFAFlow: true;
      MFASubtitleText: string;
    }
  | {
      shouldShowMFAFlow: false;
    }
);

export type CreateAeropayBankAccountPayload = {
  user_id: string;
  user_password: string;
};
export type CreateAeropayBankAccountResponse = {
  bank_account_id: string;
};
export type DeleteAeropayBankAccountPayload = {
  user_id: string;
  bank_account_id: string;
};

/**
 * This is the type returned by AeropayController.launchAchDepositFlow,
 * it dictates what action should be taken next by the caller
 */
export type AeropayACHDepositFlowResult =
  | {
      action: 'ACH_DEPOSIT_DISMISSED';
      isNewAeropayUser: boolean;
    }
  | {
      /**
       * when the user is already registered in the Aeropay network,
       * indicates that SDK has triggered the MFA flow and the user has successfully completed it
       * and the SDK should move forward and complete show the user's existing bank accounts
       */
      action: 'MFA_COMPLETED';
    }
  | {
      /**
       * indicates that the user has successfully completed the Bank Account linking flow
       * and the SDK should move forward and complete the transaction/deposit
       */
      action: 'CREATED_BANK_ACCOUNT';
      bankAccountId: string;
    }
  | {
      /**
       * indicates that the SDK has successfully completed deposit transaction
       */
      action: 'DEPOSIT_COMPLETED';
      transactions: Array<Transaction>;
    };

// Returns the theme configuration ID used to set the theme for the Aerosync BankLink component.
export const getConfigurationId = () => {
  const productionLight = 'acfb840a-38f7-4c2d-855e-f68cded2fc92';
  const stagingLight = '184697dd-f5a4-46a7-bd80-23b5e785d833';

  const LightThemePerEnvironment = new Map([
    [APIManager.Environment.QA, stagingLight],
    [APIManager.Environment.Staging, stagingLight],
    [APIManager.Environment.Prod, productionLight],
  ]);

  let lightMode = LightThemePerEnvironment.get(APIManager.CurrentEnvironment);

  if (!lightMode) {
    console.error(
      'Aerosync theme configuration ID not found for Skillz environment: ',
      APIManager.CurrentEnvironment
    );
    console.warn('Aerosync Defaulting to production light and dark theme');

    lightMode = productionLight;
  }

  return lightMode;
};

export const getAerosyncEnv = (): Environment => {
  const SkillzEnvToAerosyncEnvMap: Map<string, Environment> = new Map([
    [APIManager.Environment.QA, 'dev'],
    [APIManager.Environment.Staging, 'sandbox'],
    [APIManager.Environment.Prod, 'production'],
  ]);

  const env = SkillzEnvToAerosyncEnvMap.get(APIManager.CurrentEnvironment);

  if (!env) {
    console.error(
      'Aerosync environment not found for Skillz environment: ',
      APIManager.CurrentEnvironment
    );
    console.warn('Aerosync Defaulting to production environment');

    return 'production';
  }

  console.debug(
    `Aerosync environment: ${env} for Skillz environment: ${APIManager.CurrentEnvironment}`
  );

  return env;
};
